import { Fragment, useState, useEffect } from "react";
import {
  Typography,
  Button,
  Stack,
  Container,
  Chip,
  Box,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslator } from "../context/TranslatorContext";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

function ChipFacetSection({
  title,
  facetName,
  buckets = [],
  searchable = false,
  placeholder,
  defaultLimit = 15,
  selectedFacets = [],
  onToggle,
}) {
  const theme = useTheme();
  const { messages } = useTranslator();
  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  if (!buckets?.length) return null;

  const sortedBuckets = buckets
    .slice()
    .sort((a, b) => b.doc_count - a.doc_count);

  const query = searchQuery.trim().toLowerCase();
  const isSearching = query.length > 0;

  const matchingBuckets = isSearching
    ? sortedBuckets.filter((item) =>
        String(item.key).toLowerCase().includes(query)
      )
    : sortedBuckets;

  const isFacetSelected = (key) =>
    selectedFacets.some(
      (facet) => facet.name === facetName && String(facet.key) === String(key)
    );

  const selectedCount = selectedFacets.filter(
    (facet) => facet.name === facetName
  ).length;

  const visibleBuckets = isSearching
    ? matchingBuckets
    : showAll
    ? sortedBuckets
    : sortedBuckets.filter(
        (item, idx) => idx < defaultLimit || isFacetSelected(item.key)
      );

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "12px",
        backgroundColor: "#F8FAFC",
        border: "1px solid #E2E8F0",
        transition: "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          borderColor: "#CBD5E1",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "text.primary",
            letterSpacing: "0.01em",
          }}
        >
          {title}
        </Typography>

        {selectedCount > 0 && (
          <Chip
            size="small"
            label={`${selectedCount} selected`}
            color="primary"
            sx={{
              height: 22,
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      {searchable && (
        <OutlinedInput
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={
            placeholder ||
            `${messages.search || "Search"} ${title?.toLowerCase() || ""}...`
          }
          startAdornment={
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            </InputAdornment>
          }
          endAdornment={
            searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  edge="end"
                  sx={{ p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          sx={{
            maxWidth: { xs: "100%", sm: 360 },
            height: 36,
            fontSize: "0.85rem",
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            mb: 1.5,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#CBD5E1",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#94A3B8",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "primary.main",
              borderWidth: 1.5,
            },
            "& input": {
              py: 0.75,
              px: 0.5,
            },
          }}
          fullWidth
        />
      )}

      {isSearching && matchingBuckets.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontStyle: "italic", py: 0.5 }}
        >
          {messages.no_results || "No matching items found"}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            ...(visibleBuckets.length > 25
              ? {
                  maxHeight: 240,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": {
                    width: "5px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#CBD5E1",
                    borderRadius: "3px",
                  },
                }
              : {}),
          }}
        >
          {visibleBuckets.map((item) => {
            const isSelected = isFacetSelected(item.key);

            return (
              <Chip
                key={String(item.key)}
                onClick={() => onToggle(facetName, item.key)}
                variant={isSelected ? "filled" : "outlined"}
                sx={{
                  cursor: "pointer",
                  backgroundColor: isSelected
                    ? theme.palette.primary.dark
                    : "#ffffff",
                  borderColor: isSelected
                    ? theme.palette.primary.dark
                    : "#CBD5E1",
                  color: isSelected
                    ? theme.palette.primary.contrastText
                    : "text.primary",
                  fontWeight: isSelected ? 600 : 400,
                  "&:hover": {
                    backgroundColor: isSelected
                      ? theme.palette.primary.dark
                      : "#F1F5F9",
                    borderColor: isSelected
                      ? theme.palette.primary.dark
                      : "#94A3B8",
                  },
                }}
                label={`${item.key} (${item.doc_count})`}
              />
            );
          })}

          {!isSearching && sortedBuckets.length > defaultLimit && (
            <Chip
              variant="outlined"
              onClick={() => setShowAll((prev) => !prev)}
              label={
                showAll
                  ? messages.facets_show_less || "Show less"
                  : messages.facets_show_more || "Show more"
              }
              sx={{
                fontStyle: "italic",
                cursor: "pointer",
                backgroundColor: "#ffffff",
                borderColor: "#94A3B8",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                },
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export default function ConnectorFacets({
  facets = {},
  onSave,
  onClear,
  onClose,
  onFacetChange,
  selectedFacets: initialSelectedFacets,
}) {
  const [selectedFacets, setSelectedFacets] = useState(
    initialSelectedFacets || []
  );
  const { messages } = useTranslator();

  useEffect(() => {
    setSelectedFacets(initialSelectedFacets || []);
  }, [initialSelectedFacets]);

  const handleFacetToggle = (facetName, facetKey) => {
    const exists = selectedFacets.find(
      (facet) =>
        facet.name === facetName && String(facet.key) === String(facetKey)
    );

    let updated;
    if (exists) {
      updated = selectedFacets.filter(
        (facet) =>
          !(facet.name === facetName && String(facet.key) === String(facetKey))
      );
    } else {
      updated = [...selectedFacets, { name: facetName, key: facetKey }];
    }

    setSelectedFacets(updated);
    onFacetChange?.(updated);
  };

  const handleSave = () => {
    onSave?.(selectedFacets);
  };

  const handleClearFacets = () => {
    setSelectedFacets([]);
    onClear?.();
  };

  const facetSections = [
    {
      title: messages.facets_filter_lrType || "Asset type",
      facetName: "lrType",
      buckets: facets?.filter_lrType?.buckets,
    },
    {
      title: messages.facets_filter_mediaType || "Media type",
      facetName: "mediaType",
      buckets: facets?.filter_mediaType?.buckets,
    },
    {
      title: messages.facets_filter_modelFunction || "Model function",
      facetName: "modelFunction",
      buckets: facets?.filter_modelFunction?.buckets,
    },
    {
      title: messages.facets_filter_open || "Free of charge",
      facetName: "open",
      buckets: facets?.filter_open?.buckets,
    },
    /* {
      title: messages.facets_filter_public || "Public",
      facetName: "public",
      buckets: facets?.filter_public?.buckets,
    },
    {
      title: messages.facets_filter_external || "External",
      facetName: "external",
      buckets: facets?.filter_external?.buckets,
    },*/
    {
      title: messages.facets_filter_isUsagePolicy || "Usage policy",
      facetName: "isUsagePolicy",
      buckets: facets?.filter_isUsagePolicy?.buckets,
    },
    {
      title: messages.facets_filter_language || "Language",
      facetName: "dctLanguage",
      buckets: facets?.filter_dctLanguage?.buckets,
      searchable: true,
      placeholder: `${messages.search || "Search"} ${(
        messages.facets_filter_language || "languages"
      ).toLowerCase()}...`,
    },
    {
      title: messages.facets_filter_keyword || "Keyword",
      facetName: "keyword",
      buckets: facets?.filter_keyword?.buckets,
      searchable: true,
      placeholder: `${messages.search || "Search"} ${(
        messages.facets_filter_keyword || "keywords"
      ).toLowerCase()}...`,
    },
    {
      title: messages.facets_filter_type || "Type",
      facetName: "type",
      buckets: facets?.filter_type?.buckets,
    },
  ];

  return (
    <Fragment>
      <Container sx={{ mt: 2, mb: 2 }}>
        <Stack spacing={2}>
          {facetSections.map((section) => (
            <ChipFacetSection
              key={section.facetName}
              title={section.title}
              facetName={section.facetName}
              buckets={section.buckets}
              searchable={section.searchable}
              placeholder={section.placeholder}
              defaultLimit={15}
              selectedFacets={selectedFacets}
              onToggle={handleFacetToggle}
            />
          ))}

          {/* Save & Clear Actions */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1 }}>
            <Button variant="contained" color="primary" onClick={handleSave}>
              {messages.facets_save || "Save"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFacets}
            >
              {messages.facets_clear_filters || "Clear filters"}
            </Button>
          </Box>
        </Stack>
      </Container>
    </Fragment>
  );
}
